import type {
  Article,
  ArticleBlock,
  ArticleImage,
  Blog,
  BlogAuthor,
  BlogData,
  BlogTopic,
} from "./models";

/** First segments under an artikelside's path that are addresses of their own, never article slugs. */
export const BLOG_RESERVED_SEGMENTS = ["emne", "forfatter", "side"] as const;

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

type BlogSource = Pick<BlogData, "blogs"> & Partial<Pick<BlogData, "topics">>;

/** A request or stored path, lower-cased, decoded and without a trailing slash; undefined when it is not a path. */
export function normalizeBlogPath(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return undefined;
  try {
    const segments = trimmed
      .split("/")
      .filter(Boolean)
      .map((segment) => decodeURIComponent(segment).toLowerCase());
    if (
      segments.some(
        (segment) => [".", ".."].includes(segment) || /[\s/\\?#]/.test(segment),
      )
    )
      return undefined;
    return "/" + segments.join("/");
  } catch {
    return undefined;
  }
}

/** The artikelside's address, or undefined for a legacy artikelside that only has `detailsPageSlug`. */
export function blogPath(blog?: Pick<Blog, "path"> | null): string | undefined {
  const path = normalizeBlogPath(blog?.path);
  return path && path !== "/" ? path : undefined;
}

export function newStyleBlogs(data?: Partial<BlogSource> | null): Blog[] {
  return (Array.isArray(data?.blogs) ? data.blogs : []).filter(
    (blog) => !!blog && typeof blog.id === "string" && !!blogPath(blog),
  );
}

/** The artikelside marked »Standard«, else the first with a path. */
export function defaultNewStyleBlog(
  data?: Partial<BlogSource> | null,
): Blog | undefined {
  const blogs = newStyleBlogs(data);
  return blogs.find((blog) => blog.default === true) ?? blogs[0];
}

export function articleBlog(
  data: Partial<BlogSource> | null | undefined,
  article: Pick<Article, "blogId">,
): Blog | undefined {
  return (Array.isArray(data?.blogs) ? data.blogs : []).find(
    (blog) => blog?.id === article.blogId,
  );
}

/** `{path}/{slug}` for an article of a new-style artikelside; undefined for a legacy one. */
export function newStyleArticlePath(
  data: Partial<BlogSource> | null | undefined,
  article: Pick<Article, "blogId" | "slug">,
): string | undefined {
  const path = blogPath(articleBlog(data, article));
  return path && article.slug && SLUG.test(article.slug)
    ? `${path}/${article.slug}`
    : undefined;
}

/** Lower-case ASCII words joined by hyphens; æ, ø and å are transliterated as the CMS does for slugs. */
export function slugifyName(value: string): string {
  return value
    .toLowerCase()
    .replace(/æ/g, "ae")
    .replace(/ø/g, "oe")
    .replace(/å/g, "aa")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function authorSlug(author: Pick<BlogAuthor, "slug" | "name">): string {
  return typeof author.slug === "string" && SLUG.test(author.slug)
    ? author.slug
    : slugifyName(typeof author.name === "string" ? author.name : "");
}

export function blogTopics(data?: Partial<BlogSource> | null): BlogTopic[] {
  return (Array.isArray(data?.topics) ? data.topics : []).filter(
    (topic) =>
      !!topic &&
      typeof topic.id === "string" &&
      typeof topic.name === "string" &&
      typeof topic.slug === "string" &&
      SLUG.test(topic.slug),
  );
}

export function mainTopic(
  data: Partial<BlogSource> | null | undefined,
  article: Pick<Article, "topicIds">,
): BlogTopic | undefined {
  const topics = blogTopics(data);
  for (const id of article.topicIds ?? []) {
    const topic = topics.find((item) => item.id === id);
    if (topic) return topic;
  }
  return undefined;
}

/** The main topic's name; an article without topics may still carry a legacy `category`. */
export function articleEyebrow(
  data: Partial<BlogSource> | null | undefined,
  article: Pick<Article, "topicIds" | "category">,
): string {
  return mainTopic(data, article)?.name || article.category || "";
}

const ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

function plainText(html: string): string {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (entity, name: string) => {
      if (name[0] === "#") {
        const code =
          name[1] === "x" || name[1] === "X"
            ? parseInt(name.slice(2), 16)
            : parseInt(name.slice(1), 10);
        return Number.isFinite(code) && code > 0 && code < 0x110000
          ? String.fromCodePoint(code)
          : " ";
      }
      return ENTITIES[name.toLowerCase()] ?? entity;
    })
    .replace(/\s+/g, " ")
    .trim();
}

const hasText = (value: unknown) =>
  typeof value === "string" && !!plainText(value);
const hasUrl = (image?: ArticleImage) =>
  typeof image?.url === "string" && !!image.url.trim();

const KEEPS_EMPTY = /<(?:img|picture|video|iframe|svg|table|hr|ul|ol|li)\b/i;

/** Without the headings, paragraphs and list items that have no text, as a template's suggested heading left empty. */
export function withoutEmptyText(html: string): string {
  return html.replace(
    /<(h[2-6]|p|li)(?:\s[^>]*)?>([\s\S]*?)<\/\1\s*>/gi,
    (element, _tag: string, inner: string) =>
      plainText(inner) || KEEPS_EMPTY.test(inner) ? element : "",
  );
}

/**
 * Without an empty `altText` or `caption`, as the CMS saves them for a picture nobody described: such a picture is
 * described by its Medier description, never decorative.
 */
function describedImage(image: ArticleImage): ArticleImage {
  const { altText, caption, ...rest } = image;
  return {
    ...rest,
    ...(altText?.trim() ? { altText } : {}),
    ...(caption?.trim() ? { caption } : {}),
  };
}

/**
 * The blocks a reader sees: an article template's blocks the writer left unfilled are dropped (»Dele, du ikke
 * udfylder, vises ikke«), as are the empty headings and paragraphs of a partly filled one, and a text-and-image row
 * without its image is plain text rather than an empty picture.
 */
export function visibleArticleBlocks(blocks: ArticleBlock[]): ArticleBlock[] {
  return (Array.isArray(blocks) ? blocks : []).flatMap(
    (block): ArticleBlock[] => {
      const html =
        (block?.type === "richText" || block?.type === "imageText") &&
        typeof block.html === "string"
          ? withoutEmptyText(block.html)
          : "";
      switch (block?.type) {
        case "richText":
          return hasText(html) ? [{ ...block, html }] : [];
        case "imageText":
          return hasUrl(block.image)
            ? [{ ...block, html, image: describedImage(block.image) }]
            : hasText(html)
              ? [{ id: block.id, type: "richText", html }]
              : [];
        case "image":
          return hasUrl(block.image)
            ? [{ ...block, image: describedImage(block.image) }]
            : [];
        case "gallery": {
          const images = (block.images ?? [])
            .filter(hasUrl)
            .map(describedImage);
          return images.length ? [{ ...block, images }] : [];
        }
        case "video":
          return typeof block.url === "string" && block.url.trim()
            ? [
                block.poster
                  ? { ...block, poster: describedImage(block.poster) }
                  : block,
              ]
            : [];
        case "quote":
          return block.text?.trim() ? [block] : [];
        case "callout":
          return block.text?.trim() ? [block] : [];
        case "button":
          return block.label?.trim() && block.url?.trim() ? [block] : [];
        default:
          return [];
      }
    },
  );
}

/**
 * An article of an artikelside with a path, with only its visible blocks and with its pictures described; other
 * articles are returned as they are.
 */
export function withVisibleBlocks<T extends Article>(
  data: Partial<BlogSource> | null | undefined,
  article: T,
): T {
  return newStyleArticlePath(data, article)
    ? {
        ...article,
        ...(article.cover ? { cover: describedImage(article.cover) } : {}),
        body: visibleArticleBlocks(article.body),
      }
    : article;
}

/** The first paragraph with text of the first rich-text block with text, cut at a word after at most 200 characters. */
export function automaticExcerpt(article: Pick<Article, "body">): string {
  const block = visibleArticleBlocks(article.body).find(
    (item) => item.type === "richText",
  );
  if (!block || block.type !== "richText") return "";
  const text =
    [...block.html.matchAll(/<p(?:\s[^>]*)?>([\s\S]*?)<\/p>/gi)]
      .map((match) => plainText(match[1]))
      .find(Boolean) ?? plainText(block.html);
  if (text.length <= 200) return text;
  const cut = text.slice(0, 201);
  const end = cut.lastIndexOf(" ");
  return (
    (end > 0 ? cut.slice(0, end) : text.slice(0, 200)).replace(
      /[\s,.;:!?–-]+$/,
      "",
    ) + "…"
  );
}

export function articleExcerpt(
  article: Pick<Article, "excerpt" | "body">,
): string {
  return typeof article.excerpt === "string" && article.excerpt.trim()
    ? article.excerpt
    : automaticExcerpt(article);
}

/** The words of the blocks a reader sees, for the overview's search. */
export function articlePlainText(article: Pick<Article, "body">): string {
  return visibleArticleBlocks(article.body)
    .map((block) =>
      block.type === "richText" || block.type === "imageText"
        ? plainText(block.html)
        : block.type === "quote"
          ? block.text
          : block.type === "callout"
            ? `${block.title ?? ""} ${block.text}`
            : "",
    )
    .join(" ");
}

/** A stored `readingMinutes`, else the words of the text blocks at 200 a minute. */
export function articleReadingMinutes(
  article: Pick<Article, "readingMinutes" | "body">,
): number {
  return (
    article.readingMinutes ??
    Math.max(
      1,
      Math.ceil(
        article.body
          .map((block) =>
            block.type === "richText" || block.type === "imageText"
              ? block.html.replace(/<[^>]+>/g, " ")
              : "",
          )
          .join(" ")
          .split(/\s+/).length / 200,
      ),
    )
  );
}

const newestFirst = (a: Article, b: Article) =>
  Date.parse(b.publishedAt ?? "") - Date.parse(a.publishedAt ?? "");

export function sortNewestFirst(articles: Article[]): Article[] {
  return [...articles].sort(newestFirst);
}

/** Other articles of the same artikelside: those sharing the main topic first, each group newest first. */
export function relatedArticles(
  posts: Article[],
  current: Article,
  limit = 3,
): Article[] {
  const topic = current.topicIds?.[0];
  const shares = (post: Article) =>
    Number(!!topic && (post.topicIds ?? []).includes(topic));
  return posts
    .filter((post) => post.id !== current.id && post.blogId === current.blogId)
    .sort((a, b) => shares(b) - shares(a) || newestFirst(a, b))
    .slice(0, Math.max(1, Math.min(100, limit)));
}

export function topicPath(path: string, topic: Pick<BlogTopic, "slug">) {
  return `${path}/emne/${topic.slug}`;
}

export function authorPath(
  path: string,
  author: Pick<BlogAuthor, "slug" | "name">,
) {
  return `${path}/forfatter/${authorSlug(author)}`;
}

/** Page 1 is the base itself; later pages are `{base}/side/{n}`. */
export function overviewPagePath(base: string, page: number) {
  return page > 1 ? `${base}/side/${page}` : base;
}
